import {
  fetchEmployerConversation,
  fetchEmployerConversations,
  markConversationRead,
} from 'redux/conversations/actions';
import Authenticated from 'components/auth/Authenticated';
import EmployerLayout from 'components/layouts/employer';
import MessagePane from 'components/messages/MessagePane';
import { MessagesRemainingBanner } from 'components/subscriptions/messagesRemainingBanner';
import Head from 'next/head';
import { withRouter } from 'next/router';
import { useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Button } from 'components/Button';
import PostHog from 'components/analytics/PostHog';
import orderBy from 'lodash/orderBy';
import { UserProfileCard } from 'features/ContactCard/CardWrappers/UserProfileCard';
import { ArchiveBoxIcon, ChatIcon } from 'icons/outline';
import debounce from 'lodash/debounce';
import { fetchEmployerProfiles } from 'redux/employer/actions';
import { ContactSidebar } from 'features/ContactSidebar';
import { fetchGeographyOptions } from 'redux/geographies/actions';
import { SearchTextInput } from 'components/SearchTextInput';
import clsx from 'clsx';
import { useEmployerJobApplications } from 'hooks/useEmployerJobApplications';
import groupBy from 'lodash/groupBy';
import { fetchUserProfileMetadata } from 'redux/user_profile_metadata';
import noop from 'lodash/noop';
import { Skeleton } from 'components/Skeleton';
import { StarIcon } from 'icons/complex';
import { HiringHeroStatusbar } from 'features/HiringHeroStatusbar';
import { MessageFilterSelector } from 'components/messages/messages_filter_selector/MessageFilterSelector';

export const Messages = ({
  router,
  dispatch,
  currentUser,
  employer,
  conversationsData,
  metadata,
}) => {
  const { query, pathname } = router;
  const { conversation: queryConversationId } = query;
  const [filter, setFilter] = useState({
    userProfileName: '',
    initiatedBy: undefined,
    unreadBy: undefined,
    unrespondedTo: undefined,
    tab: 'all',
  });
  const [currentConversationId, setCurrentConversationId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [otherMember, setOtherMember] = useState(null);

  const { loading, totalPages, conversations } = conversationsData;

  const conversationIdsPrimitive = JSON.stringify(
    conversations?.map((conversation) => conversation.id),
  );
  const userMetadata = metadata.find((data) => data.id === otherMember?.id);

  useEffect(() => {
    dispatch(fetchEmployerProfiles());
  }, []);

  const userProfilesIds = conversations.map((convo) => convo.getUserProfile().id);
  const userProfilesIdsPrimitive = userProfilesIds.join(',');

  useEffect(() => {
    if (userProfilesIds.length) dispatch(fetchUserProfileMetadata(userProfilesIds));
  }, [userProfilesIdsPrimitive]);

  const setFilterHandler = async (event) => {
    event.preventDefault();
    setCurrentConversation(null);
    const {
      target: { value },
    } = event;
    setFilter((prev) => ({
      ...prev,
      userProfileName: value,
    }));
  };

  useEffect(() => {
    if (
      router.isReady &&
      queryConversationId &&
      queryConversationId !== currentConversationId &&
      conversations?.length
    ) {
      const queryConversation = conversations.find(
        (conversation) => conversation.id === queryConversationId,
      );
      if (queryConversation) {
        setCurrentConversation(queryConversation);
      } else {
        dispatch(fetchEmployerConversation(queryConversationId));
      }
    }
  }, [router.isReady, queryConversationId, conversationIdsPrimitive]);

  useEffect(() => {
    void fetchConversations(1);
  }, [
    filter.userProfileName,
    filter.tab,
    filter.initiatedBy,
    filter.unreadBy,
    filter.unrespondedTo,
  ]);

  useEffect(() => {
    dispatch(fetchGeographyOptions());
  }, []);

  const fetchConversations = async (page) => {
    setCurrentPage(page);
    await dispatch(fetchEmployerConversations(page, filter));
  };

  const setCurrentConversation = (conversation) => {
    if (!conversation) {
      router.replace(`/employer/messages`, undefined, {
        shallow: true,
      });
      setCurrentConversationId(null);
      return;
    }

    if (queryConversationId !== conversation.id) {
      router.replace(`/employer/messages?conversation=${conversation.id}`, undefined, {
        shallow: true,
      });
    }

    setCurrentConversationId(conversation.id);

    const members = conversation?.conversationMembers().toArray();
    const otherMembers = members?.filter(
      (member) => ![currentEmployerId].includes(member.messageableId),
    );

    const otherMemb = otherMembers?.[0]?.messageable() || null;
    setOtherMember(otherMemb);
    dispatch(markConversationRead(conversation.id, 'Employer'));
  };

  const currentEmployerId = employer?.employer?.id;
  const currentEmployerProfile = employer?.currentEmployerProfile;
  const currentEmployerProfileId = currentEmployerProfile?.id;

  const userProfileIds = conversations?.map(
    (conversationItem) => conversationItem.getUserProfile()?.id,
  );
  const [userProfileJobApplications, setUserProfileJobApplications] = useState({});
  const userProfilesWithoutJobApplications = userProfileIds?.filter(
    (id) => !userProfileJobApplications[id],
  );

  const {
    jobApplications,
    loading: loadingJobApplications,
    fetchJobApplications,
  } = useEmployerJobApplications();
  const jobApplicationsPrimitive = JSON.stringify(
    jobApplications.map((jobApplication) => jobApplication.id),
  );

  useEffect(() => {
    if (userProfilesWithoutJobApplications?.length === 0) return noop;

    void fetchJobApplications({
      includes: ['job_listing', 'user_profile', 'locations'],
      where: {
        user_profile_id: userProfilesWithoutJobApplications.join(','),
      },
      select: ['job_listing.title', 'user_profile.id', 'location.address'],
      limit: 100,
    });
  }, [JSON.stringify(userProfilesWithoutJobApplications)]);

  useEffect(() => {
    setUserProfileJobApplications({
      ...userProfileJobApplications,
      ...groupBy(jobApplications, (jobApplication) => jobApplication.userProfile()?.id),
    });
  }, [jobApplicationsPrimitive]);

  const handleLoadMore = async () => {
    await fetchConversations(currentPage + 1);
  };

  const resetConversations = () => {
    setCurrentConversation(null);
    void fetchConversations(1);
    void router.replace({ pathname: window.location.url, query: {} }, undefined, { shallow: true });
  };

  const debounceChangeHandler = useMemo(() => debounce(setFilterHandler, 400), []);

  const showRightSidebar = Boolean(
    currentConversationId && otherMember?.id && !loadingJobApplications,
  );

  return (
    <Authenticated employer redirectLoggedOut={true}>
      <EmployerLayout showHiringHeroBanner>
        <Head>
          <title>Messages - EarlyDay.com</title>
        </Head>
        <div className="h-[calc(100vh-10rem)] mt-10">
          <main className="relative h-full max-w-screen-xl mx-auto">
            <MessagesRemainingBanner />
            <HiringHeroStatusbar actionText="Check all your unread messages" className="mb-6" />
            <div className="flex w-full h-full gap-6">
              <div className="max-w-[308px] w-1/3 flex-1 flex flex-col gap-4 max-h-full h-full pb-8">
                <MessageFilterSelector
                  setFilter={setFilter}
                  setCurrentConversationId={setCurrentConversationId}
                />

                {loading ? (
                  <div className="flex flex-col items-center justify-start w-full h-full gap-3 overflow-hidden lg:col-span-9">
                    <Skeleton className="flex-none w-full h-24 rounded-md" />
                    <Skeleton className="flex-none w-full h-24 rounded-md" />
                    <Skeleton className="flex-none w-full h-24 rounded-md" />
                    <Skeleton className="flex-none w-full h-24 rounded-md" />
                    <Skeleton className="flex-none w-full h-24 rounded-md" />
                    <Skeleton className="flex-none w-full h-24 rounded-md" />
                    <Skeleton className="flex-none w-full h-24 rounded-md" />
                  </div>
                ) : (
                  <>
                    {(conversations?.length || null) && (
                      <div className="flex-1 overflow-auto">
                        <ul className="flex flex-col gap-2 pr-2">
                          {orderBy(
                            conversations,
                            (convo) => {
                              return new Date(convo.latestMessageAt || Date.now());
                            },
                            'desc',
                          ).map((conversation) => {
                            return (
                              <li key={conversation.id}>
                                <PostHog
                                  eventName="Select a conversation [Messages Page]"
                                  properties={{ 'Unread Messages': conversation.unreadCount }}
                                >
                                  <UserProfileCard
                                    key={conversation.id}
                                    conversation={conversation}
                                    active={
                                      Boolean(currentConversationId) &&
                                      currentConversationId === conversation.id
                                    }
                                    onClick={() => setCurrentConversation(conversation)}
                                    lastJobApplication={
                                      userProfileJobApplications[
                                        conversation.getUserProfile()?.id
                                      ]?.[0]
                                    }
                                    metadata={metadata?.find(
                                      (data) => data.id === conversation.getUserProfile()?.id,
                                    )}
                                  />
                                </PostHog>
                              </li>
                            );
                          })}
                        </ul>
                        {currentPage >= totalPages ? null : (
                          <div className="sticky bottom-0">
                            <div className="h-8 bg-gradient-to-t from-neutral-full-white to-transparent" />
                            <div className="h-4 bg-neutral-full-white" />
                            <PostHog eventName="Load more conversations [Messages Page]">
                              <Button type="button" fullWidth onClick={handleLoadMore}>
                                Load more
                              </Button>
                            </PostHog>
                          </div>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="flex flex-col flex-1 h-full">
                <div className="flex-none mb-4 flex items-center justify-start gap-2.5">
                  <PostHog
                    eventName="Chat tab selected [Messages Page]"
                    properties={{
                      user_type: 'School',
                      tab: 'all',
                    }}
                  >
                    <button
                      type="button"
                      className={clsx('flex items-center gap-2.5 px-1 font-bold', {
                        'text-neutral-ink-blue': filter.tab === 'all',
                        'text-neutral true-hover:text-neutral-dark': filter.tab !== 'all',
                      })}
                      onClick={() => {
                        setFilter((prev) => ({
                          ...prev,
                          tab: 'all',
                        }));
                        setCurrentConversation(null);
                        setCurrentConversationId(null);
                      }}
                    >
                      <ChatIcon
                        className={clsx('h-6 w-6', {
                          'text-primary-pure-blue': filter.tab === 'all',
                        })}
                      />
                      <span>All Conversations</span>
                    </button>
                  </PostHog>

                  <div className="h-full w-[1px] bg-neutral" />

                  <PostHog
                    eventName="Chat tab selected [Messages Page]"
                    properties={{
                      user_type: 'School',
                      tab: 'starred',
                    }}
                  >
                    <button
                      type="button"
                      className={clsx('flex items-center gap-2.5 px-1 font-bold', {
                        'text-neutral-ink-blue': filter.tab === 'starred',
                        'text-neutral true-hover:text-neutral-dark': filter.tab !== 'starred',
                      })}
                      onClick={() => {
                        setFilter((prev) => ({
                          ...prev,
                          tab: 'starred',
                        }));
                        setCurrentConversation(null);
                        setCurrentConversationId(null);
                      }}
                    >
                      <StarIcon className="w-6 h-6" filled={filter.tab === 'starred'} />
                      <span>Starred</span>
                    </button>
                  </PostHog>

                  <div className="h-full w-[1px] bg-neutral" />

                  <PostHog
                    eventName="Chat tab selected [Messages Page]"
                    properties={{
                      user_type: 'School',
                      tab: 'archived',
                    }}
                  >
                    <button
                      type="button"
                      className={clsx('flex items-center gap-2.5 px-1 font-bold', {
                        'text-neutral-ink-blue': filter.tab === 'archived',
                        'text-neutral true-hover:text-neutral-dark': filter.tab !== 'archived',
                      })}
                      onClick={() => {
                        setFilter((prev) => ({
                          ...prev,
                          tab: 'archived',
                        }));
                        setCurrentConversation(null);
                        setCurrentConversationId(null);
                      }}
                    >
                      <ArchiveBoxIcon
                        className={clsx('h-6 w-6', {
                          'text-primary-sunshine-yellow': filter.tab === 'archived',
                        })}
                      />
                      <span>Archived</span>
                    </button>
                  </PostHog>
                  <div className="flex ml-auto w-1/3">
                    <SearchTextInput
                      fullWidth
                      type="text"
                      onChange={debounceChangeHandler}
                      placeholder="Search by name or keyword..."
                    />
                  </div>
                </div>

                <div className="flex flex-1 h-full overflow-hidden border-t border-x border-neutral-light">
                  <div
                    className={clsx('transition-all', {
                      'w-full': !showRightSidebar,
                      'w-2/3': showRightSidebar,
                    })}
                  >
                    <MessagePane
                      key={currentConversationId}
                      {...{
                        currentConversationId,
                        conversations,
                        currentEmployerProfileId,
                        currentEmployerId,
                        currentUser,
                        resetConversations,
                        loadingConversations: loading,
                        currentTab: filter?.tab,
                      }}
                    />
                  </div>

                  <div
                    className={clsx('transition-all overflow-hidden border-neutral-light', {
                      'w-0': !showRightSidebar,
                      'w-1/3 border-l': showRightSidebar,
                    })}
                  >
                    <div className="h-full px-6 pt-5 pb-8">
                      {showRightSidebar && (
                        <ContactSidebar
                          key={otherMember.id}
                          userProfile={otherMember}
                          employerProfile={currentEmployerProfile}
                          jobApplications={userProfileJobApplications[otherMember.id]}
                          userProfileMetadata={userMetadata}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </main>
        </div>
      </EmployerLayout>
    </Authenticated>
  );
};

Messages.displayName = 'Messages';

function mapStateToProps(state) {
  return {
    conversationsData: state.conversations,
    currentUser: state.account,
    employer: state.employer,
    metadata: state.userProfileMetadata.items,
  };
}

export default connect(mapStateToProps)(withRouter(Messages));
